/**
 * Collapsible "work" card that bundles consecutive tool calls into a
 * single row in the transcript.
 *
 * Without this, every tool call on a turn pushed an extra inline row into
 * the transcript — five tool calls per analytical variable build meant five rows of
 * developer-language detail competing with the prose. Bundling them under
 * one header keeps the chat scannable: the epi sees a one-line "Worked
 * through 5 steps" by default, expands when they want the trail.
 *
 * OR-6201: the card is collapsed by default — including while tools are
 * still running. The transcript shows only the one-line header
 * ("Working… (N)" / "Worked through N steps") until the user opts in.
 * Once the user expands the row it stays open for the rest of the
 * browser session; it never auto-collapses on completion, so someone
 * watching the live tail isn't yanked shut the moment the work finishes.
 */

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import { ToolCallCard } from './ToolCallCard';
import type { EpiToolCall } from '../types/epi-chat-types';

interface ToolCallGroupCardProps {
  toolCalls: EpiToolCall[];
}

/** Aggregate status for the group header.
 *
 * Rules (in order):
 *   1. ``running`` if ANY descendant — top-level or nested sub-call — is
 *      still running. Strands fires TOOL_CALL_END for the parent's tool
 *      result separately from each sub-agent tool's TOOL_CALL_END, and
 *      arrival order is not guaranteed: the parent's end can land before
 *      a child's end. Without recursing here, the header would flip to
 *      ✓ while a sub-call spinner was still visible in the expanded body.
 *   2. ``error`` ONLY when the LAST top-level call is errored — i.e. the
 *      agent stopped on a failure rather than recovering. Intermediate
 *      errors that the agent retried past (e.g. the first
 *      ``submit_analytical_variable`` failed and the agent re-ran it successfully)
 *      do NOT propagate to the header. Showing the group as failed when
 *      the workflow actually completed misrepresents the outcome and
 *      misdirects the user's attention to a recovered hiccup.
 *   3. Otherwise ``complete``.
 *
 * Sub-call errors are visible on their own row when the user expands the
 * group; they don't need to bleed into the header.
 */
const aggregateStatus = (calls: EpiToolCall[]): 'running' | 'complete' | 'error' => {
  const isAnyDescendantRunning = (call: EpiToolCall): boolean =>
    call.status === 'running' || (call.subCalls ?? []).some(isAnyDescendantRunning);
  if (calls.some(isAnyDescendantRunning)) return 'running';
  const last = calls[calls.length - 1];
  if (last && last.status === 'error') return 'error';
  return 'complete';
};

/** Total step count for the header — every tool call in the tree, not just
 * the top-level ones. A concept-group build nests its real work (OMOP
 * searches, descendant counts, prevalence probes) two levels deep under a
 * single ``select_concept_group`` delegate, so ``toolCalls.length`` alone
 * reads "1 step" and hides the depth of work. Counting descendants keeps
 * the header honest about how much happened (OR-6201). */
const countSteps = (calls: EpiToolCall[]): number =>
  calls.reduce((total, call) => total + 1 + countSteps(call.subCalls ?? []), 0);

export const ToolCallGroupCard = ({ toolCalls }: ToolCallGroupCardProps) => {
  const status = aggregateStatus(toolCalls);
  // OR-6328: with the flag ON, expanding the working state shows only each
  // step's plain-language label — raw tool names, args, results, and
  // sub-agent reasoning stay hidden (researcher-facing "reason" text is the
  // one carve-out, handled per row in ToolCallCard). Flag OFF (internal
  // users) keeps the full detail. Presentation-only: the durable ui# events
  // and telemetry behind these rows are emitted server-side regardless.
  // TODO: cleanup with research_assistant_hide_tool_internals ff
  const hideInternals = Boolean(useFeatureFlagSplit('research_assistant_hide_tool_internals'));
  // OR-6201: collapsed by default, even while running — the header alone
  // ("Working… (N)") is all the top level shows until the user opts in.
  // No auto-expand and no auto-collapse: once the user opens the row it
  // stays open for the session (the transcript isn't virtualized, so this
  // local state survives as long as the chat is mounted).
  const [expanded, setExpanded] = useState(false);

  const headerIcon =
    status === 'running' ? (
      <CircularProgress size={11} thickness={5} sx={{ color: 'text.secondary' }} />
    ) : status === 'error' ? (
      <ErrorOutlineIcon sx={{ fontSize: '0.9rem', color: 'error.main' }} />
    ) : (
      <CheckRoundedIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />
    );

  const stepCount = countSteps(toolCalls);
  const headerLabel =
    status === 'running'
      ? `Working… (${stepCount})`
      : `Worked through ${stepCount} step${stepCount === 1 ? '' : 's'}`;

  return (
    <Box sx={{ marginY: 0.5 }}>
      <Stack
        component="button"
        direction="row"
        spacing={0.75}
        alignItems="center"
        onClick={() => setExpanded((p) => !p)}
        sx={{
          background: 'none',
          border: 0,
          padding: 0,
          width: '100%',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'text.secondary',
          minHeight: 22,
          '&:hover .work-caret': { color: 'text.primary' },
          '&:focus-visible': {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
            borderRadius: 1,
          },
        }}
        aria-expanded={expanded}
        data-testid="tool-call-group-toggle"
      >
        {headerIcon}
        <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
          {headerLabel}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          className="work-caret"
          sx={{ color: 'text.secondary', paddingLeft: 0.5 }}
          aria-hidden
        >
          {expanded ? '▾' : '▸'}
        </Typography>
      </Stack>
      {expanded && (
        <Box
          sx={{
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
            paddingLeft: 1.25,
            marginLeft: 0.5,
            marginTop: 0.5,
          }}
        >
          {toolCalls.map((toolCall) => (
            <ToolCallCard key={toolCall.toolId} toolCall={toolCall} hideInternals={hideInternals} />
          ))}
        </Box>
      )}
    </Box>
  );
};
